import type {
  DescService,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf';
import { Code } from '@grpc-gen/google/rpc/code_pb';
import { GrpcTransport } from './grpc-transport';
import { GrpcMetaValue } from './grpc-transport-context';
import type { Observable } from 'rxjs';
import { defer, tap } from 'rxjs';

import { getMethod } from './get-method';
import { GrpcError } from './grpc-error';
import { FETCH_ORIGIN } from './grpc-fetch-origin';
import type { GrpcNext } from './grpc-next';
import { GrpcOutput } from './grpc-output';
import type { GrpcContext } from './grpc-transport-context';
import { LOGGER_SUFFIX_META } from './logger-suffix.meta';
import { generateStatusInfoEntries, generateStatusInfoMap } from './utils/enum';

export class LoggerGrpcTransport extends GrpcTransport {
  constructor(private readonly transport: GrpcTransport) {
    super();
  }

  protected internalSummon<S extends DescService, M extends keyof S['method']>(
    service: S,
    methodName: M,
    input: NoInfer<MessageInitShape<S['method'][M]['input']>>,
    context: GrpcContext,
  ): Observable<GrpcNext<S['method'][M]['output']>> {
    const method = getMethod(service, methodName);

    const result = GrpcTransport.summon<S, M>(
      this.transport,
      service,
      methodName,
      input,
      context,
    );

    return defer(() => {
      const startTime = performance.now();
      const outputs: MessageShape<S['method'][M]['output']>[] = [];
      let origin: string | null = null;
      const suffixes: string[] = [];

      return result.pipe(
        tap({
          next: (next) => {
            if (next instanceof GrpcMetaValue) {
              if (next.is(FETCH_ORIGIN)) {
                origin = next.value;
                return;
              }

              if (next.is(LOGGER_SUFFIX_META)) {
                suffixes.push(next.value);
                return;
              }
            }

            if (next instanceof GrpcOutput) {
              outputs.push(next.message);
            }
          },
          complete: () => {
            const endTime = performance.now();
            const headerSuffix =
              suffixes.length === 0
                ? `(${(endTime - startTime).toFixed(2)} ms)`
                : suffixes.join(' ');

            console.groupCollapsed(
              '%c[GRPC]%c️ %s/%c%s%c %s',
              'color: #00bcd4',
              'font-weight: normal; color: gray',
              service.typeName,
              'font-weight: bold',
              method.name,
              'font-weight: normal',
              headerSuffix,
            );
            console.log('%cRequest%c', 'color: #884ea0', '', input);

            for (const output of outputs) {
              console.log('%cResponse%c', 'color: #17a589', '', output);
            }

            console.groupEnd();
          },
          error: (error) => {
            const endTime = performance.now();
            const timeElapsed = endTime - startTime;

            let status = Code.UNKNOWN;

            if (error instanceof GrpcError) {
              status = error.code;
            }

            const errorStatusesMap = generateStatusInfoMap(
              Code,
              generateStatusInfoEntries(Code),
            );

            const displayStatus = errorStatusesMap
              ? errorStatusesMap.get(status)?.label
              : 'Unknown';

            console.groupCollapsed(
              '%c[GRPC %s]%c️ %s/%c%s%c (%s ms)',
              'color: #ff0000',
              displayStatus,
              'font-weight: normal; color: gray',
              service.typeName,
              'font-weight: bold',
              method.name,
              'font-weight: normal',
              timeElapsed.toFixed(2),
            );
            console.log('%cRequest%c', 'color: #884ea0', '', input);
            console.log('%cError%c', 'color: #17a589', '', error);
            console.groupEnd();
          },
        }),
      );
    });
  }
}
